import * as Dialog from "@radix-ui/react-dialog";
import { Copy, Mail } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { emailCredentials, mailStatus, resetUserPassword } from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/email";

import { MAIL_STATUS_KEY } from "./MailPanel";
import { passwordStrength } from "./lookups";

export interface ResetPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserPublic | null;
}

/**
 * Reset password flow.
 *
 * The backend is the source of truth for the temporary password
 * (``POST /admin/users/{id}/reset-password`` returns a generated value).
 * We surface the returned value here so an admin can communicate it
 * out-of-band — phone, in-person — and never via the same channel as
 * the username.
 *
 * The strength meter on this dialog is *advisory* — it rates the value
 * the backend returned. The admin-facing value of seeing the strength
 * is to confirm the generator has not regressed; admins cannot edit it.
 *
 * Emailing it is offered only once mail is genuinely working, and only
 * as a deliberate press — never automatically on reset. The password
 * has already changed by then, so a send that fails changes nothing
 * except that the admin must read it out instead.
 */
export function ResetPasswordModal({
  open,
  onOpenChange,
  user,
}: ResetPasswordModalProps) {
  const { toast } = useToast();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Editable: a candidate's username is not always their address, and
  // an account may have none recorded at all.
  const [address, setAddress] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Opt IN, and the whole email affordance hangs off it -- the address
  // box and the Send button both appear only once this is ticked. Same
  // stance as Create user: a reset is complete without sending anything,
  // and the ordinary way to hand over a temporary password here is still
  // to read it out. Never defaulted on.
  const [sendByEmail, setSendByEmail] = useState(false);

  const mail = useQuery({
    queryKey: MAIL_STATUS_KEY,
    queryFn: mailStatus,
    enabled: open,
  });
  const mailReady = mail.data?.available ?? false;

  useEffect(() => {
    if (open) {
      setTempPassword(null);
      setCopied(false);
      setSentTo(null);
      setSendByEmail(false);
      setAddress(user?.email ?? "");
    }
  }, [open, user]);

  const reset = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("no user");
      return resetUserPassword(user.id);
    },
    onSuccess: (data) => {
      setTempPassword(data.temporary_password);
      toast({
        title: "Password reset",
        description: "Communicate the temporary password by phone or in person.",
        variant: "success",
        duration: 6_000,
      });
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "Reset failed";
      toast({ title: "Reset failed", description: detail, variant: "error" });
    },
  });

  const send = useMutation({
    mutationFn: () => {
      if (!user || !tempPassword) throw new Error("nothing to send");
      return emailCredentials(user.id, {
        password: tempPassword,
        email: address.trim(),
      });
    },
    onSuccess: (r) => {
      if (r.sent) {
        setSentTo(r.to);
        toast({
          title: `Login sent to ${r.to}`,
          variant: "success",
          duration: 4_000,
        });
      } else {
        toast({
          title: "Not sent",
          ...(r.detail ? { description: r.detail } : {}),
          variant: "error",
          duration: 8_000,
        });
      }
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? "The email did not send";
      toast({ title: "Not sent", description: detail, variant: "error" });
    },
  });

  const strength = useMemo(
    () => passwordStrength(tempPassword ?? ""),
    [tempPassword],
  );

  const onCopy = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      toast({ title: "Copied to clipboard", variant: "default", duration: 2_000 });
      // The clipboard now holds PHI-adjacent material — auto-clear after
      // 30s so it doesn't linger on a shared workstation.
      setTimeout(() => {
        navigator.clipboard.writeText("").catch(() => undefined);
      }, 30_000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Browser blocked clipboard. Read the password aloud instead.",
        variant: "error",
      });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-lg"
          data-cy="reset-password-modal"
        >
          <div className="border-b border-border px-6 pt-6 pb-4">
            <Dialog.Title className="text-lg font-semibold text-foreground-heading">
              Reset password
            </Dialog.Title>
            {user ? (
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                Resetting password for{" "}
                <span className="font-mono text-foreground">{user.username}</span>
                {user.full_name ? ` · ${user.full_name}` : ""}
              </Dialog.Description>
            ) : null}
          </div>

          <div className="px-6 py-5 space-y-4">
            <div
              className="rounded-md border-l-4 border-status-wip bg-status-wip-bg px-3 py-2 text-xs text-foreground"
              data-cy="reset-callout"
            >
              <div className="font-semibold">
                {mailReady
                  ? "Send this password, or read it out. Do not put it in chat."
                  : "Communicate this password by phone or in person."}
              </div>
              <div className="mt-1 text-foreground-muted">
                {mailReady
                  ? "The recipient must change it on first login."
                  : "Do not send it by email or chat. The recipient must change it on first login."}
              </div>
            </div>

            {tempPassword ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-foreground-muted">
                    Temporary password
                  </div>
                  <div
                    data-cy="temp-password"
                    className="sk-input mt-1 select-all font-mono tracking-wider w-auto"
                  >
                    {tempPassword}
                  </div>
                </div>

                <div data-cy="strength-meter">
                  <div className="flex items-center justify-between text-xs">
                    <span className="sk-label">Strength</span>
                    <span
                      data-cy="strength-label"
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${strength.pillTone}`}
                    >
                      {strength.label}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-border">
                    <div
                      className={`h-full ${strength.color} transition-all`}
                      style={{ width: `${(strength.score / 4) * 100}%` }}
                    />
                  </div>
                </div>

                {mailReady ? (
                  <>
                    {/* The gate. Ticking this is what reveals the address
                        box and the Send button; until then the dialog is
                        just "here is the password, read it out". */}
                    <label
                      data-cy="field-send-by-email"
                      className="flex items-start gap-2 rounded-lg border border-border px-3 py-2 text-xs text-foreground"
                    >
                      <input
                        type="checkbox"
                        data-cy="input-send-by-email"
                        checked={sendByEmail}
                        onChange={(e) => setSendByEmail(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-accent"
                      />
                      <span>
                        Send via email
                        <span className="mt-0.5 block text-[11px] text-foreground-muted">
                          {sentTo
                            ? `Already sent to ${sentTo}. Unticking does not un-send it.`
                            : "Otherwise, read the password out — it has already been changed either way."}
                        </span>
                      </span>
                    </label>

                    {sendByEmail ? (
                  <label className="block text-xs" data-cy="field-send-to">
                    <span className="sk-label">Send to</span>
                    <input
                      type="email"
                      data-cy="input-send-to"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder={
                        user?.email
                          ? user.email
                          : "This account has no email address on file"
                      }
                      className={[
                        "sk-input mt-1",
                        // Marked only once something has been typed: an
                        // empty box on a dialog nobody has to send from
                        // is not an error, it is a choice not yet made.
                        address.trim().length > 0 && !isValidEmail(address)
                          ? "sk-invalid"
                          : "",
                      ].join(" ")}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <span
                      className={[
                        "mt-1 block text-[11px]",
                        address.trim().length > 0 && !isValidEmail(address)
                          ? "text-status-urgent"
                          : "text-foreground-muted",
                      ].join(" ")}
                    >
                      {address.trim().length > 0 && !isValidEmail(address)
                        ? "That is not a complete email address, so Send is unavailable."
                        : sentTo
                          ? `Sent to ${sentTo}. Sending again is allowed — the password does not change.`
                          : !user?.email
                            ? "This account has no address on file. Type one to send to it — this does not change the account."
                            : address.trim() !== user.email
                              ? // Say so. Sending a login somewhere other
                                // than the recorded address is a
                                // legitimate thing to do and a careless
                                // thing to do by accident, and the box
                                // looks identical either way.
                                `Not the address on file (${user.email}). Sending here does not change the account.`
                              : "Their address on file. Edit it to send somewhere else — this does not change the account."}
                    </span>
                  </label>
                    ) : null}
                  </>
                ) : (
                  <p
                    data-cy="reset-mail-off"
                    className="text-[11px] text-foreground-muted"
                  >
                    {mail.data?.reason ??
                      "Mail is not configured, so this cannot be emailed."}{" "}
                    Set it up under Admin → Mail.
                  </p>
                )}
              </div>
            ) : null}
          </div>

          {tempPassword ? (
            <div className="flex justify-end gap-2 border-t border-border bg-table-row-alt px-6 py-4">
              <button
                type="button"
                onClick={onCopy}
                data-cy="btn-copy-password"
                title={copied ? "Copied" : "Copy the password"}
                aria-label="Copy the password"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 py-1.5 text-sm text-foreground hover:bg-table-row-hover"
              >
                <Copy aria-hidden className="h-4 w-4" />
                {copied ? "Copied!" : "Copy"}
              </button>
              {/* Follows the tick, not just `mailReady`. A Send button
                  sitting next to a hidden address box would be offering
                  to send to somewhere the admin cannot see. */}
              {mailReady && sendByEmail && (
                <button
                  type="button"
                  data-cy="btn-send-password"
                  disabled={send.isPending || !isValidEmail(address)}
                  title={
                    isValidEmail(address)
                      ? `Email the login to ${address.trim()}`
                      : address.trim().length === 0
                        ? "Enter an address to send to"
                        : `“${address.trim()}” is not a complete email address`
                  }
                  onClick={() => send.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 py-1.5 text-sm text-foreground hover:bg-table-row-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Mail aria-hidden className="h-4 w-4" />
                  {send.isPending ? "Sending…" : sentTo ? "Send again" : "Send"}
                </button>
              )}
              <Dialog.Close
                data-cy="btn-close-reset"
                className="sk-btn sk-btn-primary sk-sm"
              >
                Done
              </Dialog.Close>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2 border-t border-border bg-table-row-alt px-6 py-4">
              <Dialog.Close
                data-cy="btn-cancel-reset"
                className="rounded-lg border border-border bg-transparent px-3 py-1.5 text-sm text-foreground hover:bg-table-row-hover"
              >
                Cancel
              </Dialog.Close>
              <button
                type="button"
                onClick={() => reset.mutate()}
                disabled={!user || reset.isPending}
                data-cy="btn-confirm-reset"
                className="rounded-lg bg-status-urgent px-3 py-1.5 text-sm font-semibold text-white hover:bg-status-urgent/90 disabled:opacity-50"
              >
                {reset.isPending ? "Resetting…" : "Generate temporary password"}
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
